import React, { useState, useEffect } from 'react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import './RetirementPlanner.css';

/**
 * Componente de Planejamento de Aposentadoria
 * Permite simular a renda passiva e estimar o tempo para independência financeira
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.initialFormData - Dados iniciais do formulário da calculadora
 */
const RetirementPlanner = ({ initialFormData }) => {
  // Estado para os dados do planejamento
  const [planData, setPlanData] = useState({
    currentAge: 30,
    retirementAge: 65,
    lifeExpectancy: 85,
    monthlyExpenses: 5000,
    withdrawalRate: 4, // Taxa de retirada segura (% ao ano)
    inflationRate: 4,  // Taxa de inflação estimada (% ao ano)
    adjustForInflation: true,
  });
  
  // Estado para os resultados calculados
  const [results, setResults] = useState({
    targetAmount: 0,
    passiveIncome: 0,
    yearsToRetirement: 0,
    retirementFundDuration: 0,
    isFinanciallyIndependent: false,
    chartData: []
  });
  
  // Atualiza dados do formulário
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPlanData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };
  
  // Alterna entre considerar inflação ou não
  const toggleInflationAdjustment = () => {
    setPlanData(prev => ({
      ...prev,
      adjustForInflation: !prev.adjustForInflation
    }));
  };
  
  // Calcula os resultados com base nos dados do formulário
  useEffect(() => {
    if (!initialFormData) return;
    
    // Extrai dados da calculadora principal
    const { initialCapital, monthlyContribution, interestRate, period } = initialFormData;
    
    // Converte taxas anuais para mensais
    const monthlyWithdrawalRate = planData.withdrawalRate / 100 / 12;
    const monthlyInflationRate = planData.inflationRate / 100 / 12;
    const monthlyInterestRate = interestRate / 100;
    
    // Calcula o valor alvo necessário para gerar renda passiva desejada
    const monthlyExpensesAdjusted = planData.adjustForInflation 
      ? adjustForInflation(planData.monthlyExpenses, monthlyInflationRate, (planData.retirementAge - planData.currentAge) * 12)
      : planData.monthlyExpenses;
    
    const targetAmount = monthlyExpensesAdjusted * 12 / (planData.withdrawalRate / 100);
    
    // Calcula renda passiva mensal com o capital atual (projetado)
    let finalCapital = calculateFinalAmount(initialCapital, monthlyContribution, monthlyInterestRate, period);
    const passiveIncome = finalCapital * monthlyWithdrawalRate;
    
    // Determina se a pessoa já atingiu independência financeira
    const isFinanciallyIndependent = passiveIncome >= monthlyExpensesAdjusted;
    
    // Calcula anos até a aposentadoria (para atingir o valor alvo)
    const yearsToRetirement = calculateYearsToTarget(
      initialCapital,
      monthlyContribution,
      monthlyInterestRate,
      targetAmount
    );
    
    // Calcula duração do fundo de aposentadoria
    const retirementFundDuration = calculateFundDuration(
      finalCapital,
      monthlyExpensesAdjusted,
      monthlyInterestRate,
      monthlyInflationRate
    );
    
    // Gera dados para o gráfico
    const chartData = generateChartData(
      initialCapital,
      monthlyContribution,
      monthlyInterestRate,
      monthlyExpensesAdjusted,
      monthlyWithdrawalRate,
      monthlyInflationRate
    );
    
    // Atualiza os resultados
    setResults({
      targetAmount,
      passiveIncome,
      yearsToRetirement,
      retirementFundDuration,
      isFinanciallyIndependent,
      chartData
    });
    
  }, [initialFormData, planData]);
  
  // Função para ajustar um valor pela inflação
  const adjustForInflation = (currentValue, monthlyInflationRate, months) => {
    return currentValue * Math.pow(1 + monthlyInflationRate, months);
  };
  
  // Calcula o valor final após período de investimento
  const calculateFinalAmount = (initialCapital, monthlyContribution, monthlyRate, months) => {
    if (monthlyRate <= 0) {
      return initialCapital + (monthlyContribution * months);
    }
    
    return initialCapital * Math.pow(1 + monthlyRate, months) + 
           monthlyContribution * (Math.pow(1 + monthlyRate, months) - 1) / monthlyRate;
  };
  
  // Calcula anos necessários para atingir valor alvo
  const calculateYearsToTarget = (initialCapital, monthlyContribution, monthlyRate, targetAmount) => {
    if (initialCapital >= targetAmount) {
      return 0;
    }
    
    if (monthlyRate <= 0 && monthlyContribution <= 0) {
      return Infinity;
    }
    
    if (monthlyRate <= 0) {
      return (targetAmount - initialCapital) / (monthlyContribution * 12);
    }
    
    try {
      // Fórmula para calcular o tempo necessário (em meses)
      const numerator = Math.log((targetAmount * monthlyRate + monthlyContribution) / 
                                 (initialCapital * monthlyRate + monthlyContribution));
      const denominator = Math.log(1 + monthlyRate);
      const months = Math.ceil(numerator / denominator);
      
      return months / 12; // Converte para anos
    } catch (error) {
      console.error("Erro ao calcular anos para o alvo:", error);
      return 0;
    }
  };
  
  // Calcula a duração estimada do fundo de aposentadoria
  const calculateFundDuration = (capital, monthlyExpenses, monthlyRate, inflationRate) => {
    if (capital <= 0 || monthlyExpenses <= 0) {
      return 0;
    }
    
    // Se a renda passiva for maior que as despesas, o fundo dura para sempre
    if (capital * monthlyRate >= monthlyExpenses) {
      return Infinity;
    }
    
    // Taxa real de juros (considerando inflação)
    const realRate = (1 + monthlyRate) / (1 + inflationRate) - 1;
    
    if (realRate <= 0) {
      // Caso simples: apenas dividir o capital pelas despesas mensais
      return capital / monthlyExpenses / 12;
    }
    
    // Fórmula para duração de fundo com retiradas e rendimentos
    try {
      const duration = Math.log(1 - (monthlyExpenses * realRate * capital)) / 
                       Math.log(1 + realRate);
      
      // Verifica se o resultado é válido
      if (isNaN(duration) || !isFinite(duration)) {
        return capital / monthlyExpenses / 12; // Fallback simples
      }
      
      return Math.abs(duration) / 12; // Converte meses para anos
    } catch (error) {
      console.error("Erro ao calcular duração do fundo:", error);
      return capital / monthlyExpenses / 12; // Fallback simples
    }
  };
  
  // Gera dados para o gráfico
  const generateChartData = (
    initialCapital,
    monthlyContribution,
    monthlyRate,
    monthlyExpenses,
    withdrawalRate,
    inflationRate
  ) => {
    const data = [];
    const maxYears = 50; // Limitamos a 50 anos para o gráfico
    
    let capital = initialCapital;
    let expenses = monthlyExpenses;
    let accumulationPhase = true;
    const targetForRetirement = monthlyExpenses * 12 / (planData.withdrawalRate / 100);
    
    for (let year = 0; year <= maxYears; year++) {
      // Verifica se atingiu o valor alvo para aposentadoria
      if (accumulationPhase && capital >= targetForRetirement) {
        accumulationPhase = false;
      }
      
      // Durante a fase de acumulação, adiciona aportes mensais
      if (accumulationPhase) {
        // Simula 12 meses de aportes e crescimento
        for (let month = 0; month < 12; month++) {
          capital = capital * (1 + monthlyRate) + monthlyContribution;
        }
      } else {
        // Fase de retirada (aposentadoria)
        // Ajusta despesas pela inflação anual
        if (year > 0) {
          expenses = expenses * Math.pow(1 + inflationRate, 12);
        }
        
        // Simula 12 meses de retiradas e crescimento
        for (let month = 0; month < 12; month++) {
          capital = capital * (1 + monthlyRate) - expenses;
          
          // Não permite capital negativo no gráfico
          if (capital < 0) {
            capital = 0;
            break;
          }
        }
      }
      
      data.push({
        year,
        capital,
        expenses: expenses * 12, // Despesas anuais
        phase: accumulationPhase ? 'accumulation' : 'retirement',
        passiveIncome: capital * withdrawalRate * 12 // Renda passiva anual
      });
      
      // Para de calcular se o capital acabou
      if (capital <= 0) break;
    }
    
    return data;
  };
  
  // Formata valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  // Formata percentuais
  const formatPercent = (value) => {
    return `${value}%`;
  };
  
  // Formata anos (com mensagem de "infinito" se necessário)
  const formatYears = (years) => {
    if (years === Infinity) return "Perpétuo";
    if (years >= 100) return "100+ anos";
    
    const wholeYears = Math.floor(years);
    const months = Math.round((years - wholeYears) * 12);
    
    if (months === 0) {
      return `${wholeYears} ano${wholeYears !== 1 ? 's' : ''}`;
    }
    
    return `${wholeYears} ano${wholeYears !== 1 ? 's' : ''} e ${months} mês(es)`;
  };
  
  // Tooltip personalizado para o gráfico
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="retirement-tooltip">
          <p className="tooltip-year">Ano {label}</p>
          <p className="tooltip-phase">
            Fase: {data.phase === 'accumulation' ? 'Acumulação' : 'Aposentadoria'}
          </p>
          <p className="tooltip-capital">
            Capital: {formatCurrency(data.capital)}
          </p>
          <p className="tooltip-income">
            Renda passiva anual: {formatCurrency(data.passiveIncome)}
          </p>
          {data.phase === 'retirement' && (
            <p className="tooltip-expenses">
              Despesas anuais: {formatCurrency(data.expenses)}
            </p>
          )}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="retirement-planner">
      <h2 className="retirement-planner-title">
        Planejamento de Aposentadoria
      </h2>
      
      <p className="retirement-intro">
        Planeje sua independência financeira e descubra quanto tempo falta para você se aposentar.
      </p>
      
      <div className="retirement-content">
        <div className="retirement-form">
          <div className="retirement-form-group">
            <label htmlFor="currentAge">Idade atual</label>
            <input
              type="number"
              id="currentAge"
              name="currentAge"
              value={planData.currentAge}
              onChange={handleInputChange}
              min="0"
              max="100"
            />
            <span className="input-suffix">anos</span>
          </div>
          
          <div className="retirement-form-group">
            <label htmlFor="retirementAge">Idade desejada para aposentadoria</label>
            <input
              type="number"
              id="retirementAge"
              name="retirementAge"
              value={planData.retirementAge}
              onChange={handleInputChange}
              min={planData.currentAge}
              max="100"
            />
            <span className="input-suffix">anos</span>
          </div>
          
          <div className="retirement-form-group">
            <label htmlFor="lifeExpectancy">Expectativa de vida</label>
            <input
              type="number"
              id="lifeExpectancy"
              name="lifeExpectancy"
              value={planData.lifeExpectancy}
              onChange={handleInputChange}
              min={planData.retirementAge}
              max="120"
            />
            <span className="input-suffix">anos</span>
          </div>
          
          <div className="retirement-form-group">
            <label htmlFor="monthlyExpenses">Despesas mensais na aposentadoria</label>
            <input
              type="number"
              id="monthlyExpenses"
              name="monthlyExpenses"
              value={planData.monthlyExpenses}
              onChange={handleInputChange}
              min="0"
              step="100"
            />
            <span className="input-suffix">R$</span>
          </div>
          
          <div className="retirement-form-group">
            <label htmlFor="withdrawalRate">Taxa de retirada segura</label>
            <input
              type="number"
              id="withdrawalRate"
              name="withdrawalRate"
              value={planData.withdrawalRate}
              onChange={handleInputChange}
              min="0.1"
              max="10"
              step="0.1"
            />
            <span className="input-suffix">% ao ano</span>
          </div>
          
          <div className="retirement-form-group">
            <label htmlFor="inflationRate">Inflação estimada</label>
            <input
              type="number"
              id="inflationRate"
              name="inflationRate"
              value={planData.inflationRate}
              onChange={handleInputChange}
              min="0"
              max="20"
              step="0.5"
            />
            <span className="input-suffix">% ao ano</span>
          </div>
          
          <div className="retirement-form-group form-checkbox">
            <input
              type="checkbox"
              id="adjustForInflation"
              checked={planData.adjustForInflation}
              onChange={toggleInflationAdjustment}
            />
            <label htmlFor="adjustForInflation">
              Ajustar valores pela inflação
            </label>
          </div>
        </div>
        
        <div className="retirement-results">
          <div className="retirement-stats">
            <div className="retirement-stat-card">
              <div className="retirement-stat-title">Capital Necessário</div>
              <div className="retirement-stat-value">{formatCurrency(results.targetAmount)}</div>
              <div className="retirement-stat-desc">
                Para gerar uma renda passiva mensal de {formatCurrency(planData.monthlyExpenses)}
              </div>
            </div>
            
            <div className="retirement-stat-card">
              <div className="retirement-stat-title">Tempo até a Aposentadoria</div>
              <div className="retirement-stat-value">{formatYears(results.yearsToRetirement)}</div>
              <div className="retirement-stat-desc">
                Com seus investimentos atuais
              </div>
            </div>
            
            <div className="retirement-stat-card">
              <div className="retirement-stat-title">Renda Passiva Mensal</div>
              <div className="retirement-stat-value">
                {formatCurrency(results.passiveIncome)}
              </div>
              <div className="retirement-stat-desc">
                {results.isFinanciallyIndependent 
                  ? "Você já atingiu independência financeira!" 
                  : `${formatPercent(Math.round(results.passiveIncome / planData.monthlyExpenses * 100))} das suas despesas cobertas`}
              </div>
            </div>
            
            <div className="retirement-stat-card">
              <div className="retirement-stat-title">Duração do Fundo</div>
              <div className="retirement-stat-value">
                {formatYears(results.retirementFundDuration)}
              </div>
              <div className="retirement-stat-desc">
                {results.retirementFundDuration === Infinity 
                  ? "Seu capital é perpétuo" 
                  : "Tempo estimado até esgotar o fundo"}
              </div>
            </div>
          </div>
          
          <div className="retirement-chart">
            <h3>Projeção do Patrimônio</h3>
            
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart
                data={results.chartData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.6} />
                <XAxis 
                  dataKey="year" 
                  label={{ 
                    value: 'Anos', 
                    position: 'insideBottomRight', 
                    offset: -10 
                  }} 
                />
                <YAxis 
                  tickFormatter={(value) => formatCurrency(value)} 
                  width={80}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                
                {/* Linha de referência para o valor alvo */}
                <ReferenceLine 
                  y={results.targetAmount} 
                  stroke="#ff7300"
                  strokeDasharray="3 3"
                  label={{ 
                    value: 'Capital necessário', 
                    position: 'top',
                    fill: '#ff7300'
                  }} 
                />
                
                <Area 
                  type="monotone" 
                  dataKey="capital" 
                  name="Patrimônio Total" 
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  fillOpacity={0.5}
                />
                
                <Area 
                  type="monotone" 
                  dataKey="passiveIncome" 
                  name="Renda Passiva Anual" 
                  stroke="#82ca9d" 
                  fill="#82ca9d" 
                  fillOpacity={0.5} 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          
          <div className="retirement-tips">
            <h3>Dicas para Independência Financeira</h3>
            <ul className="retirement-tips-list">
              <li>
                <strong>Regra dos 4%:</strong> Uma taxa de retirada segura tradicionalmente 
                considerada é 4% ao ano. Isso significa que para cada R$100 mensais que você 
                precisa, deve acumular cerca de R$30.000.
              </li>
              <li>
                <strong>Impacto da inflação:</strong> Lembre-se que a inflação diminui o poder de 
                compra ao longo do tempo. Considere isso em seus cálculos.
              </li>
              <li>
                <strong>Diversificação:</strong> Não dependa de uma única fonte de renda na 
                aposentadoria. Diversifique seus investimentos entre renda fixa e variável.
              </li>
              <li>
                <strong>Reavalie periodicamente:</strong> Os planos de aposentadoria devem ser 
                revistos anualmente, ajustando conforme suas circunstâncias mudam.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetirementPlanner;
